import React, { useState, useMemo, useRef, useEffect } from 'react';
import axios from 'axios'; // Import axios for making HTTP requests
import { Search, Calendar, AlertCircle, ChevronLeft, ChevronRight, User, Sword } from 'lucide-react';
import { champions } from './champions';  // Add this import at the top with other imports
import ChampionAutocomplete from './components/ChampionAutocomplete';  // Add this import
import SummonerPage from './components/SummonerPage';
import ReactMarkdown from 'react-markdown';
import './styles/fonts.css';

// Helper function to format dates
function formatDate(date) {
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// Custom Date Picker Component
const CustomDatePicker = ({ selectedDate, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const datePickerRef = useRef(null);

  // Effect to handle clicking outside the date picker to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [datePickerRef]);

  // Calculate the number of days in the current month
  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();
  // Calculate the day of the week the first day of the month falls on (0-6, where 0 is Sunday)
  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  // Create arrays for the days of the month and the padding days from the previous month
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const previousMonthDays = Array.from({ length: firstDayOfMonth }, (_, i) => i + 1);

  // Handler for moving to the previous month
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Handler for moving to the next month
  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Handler for selecting a date
  const handleDateClick = (day) => {
    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    onChange(newDate);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={datePickerRef}>
      {/* Date display field */}
      <div
        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-100 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        {selectedDate ? formatDate(selectedDate) : 'Select Date'}
      </div>
      {/* Calendar icon */}
      <Calendar
        className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      />
      {/* Date picker dropdown */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-lg z-10 p-2">
          {/* Month navigation */}
          <div className="flex justify-between items-center mb-2">
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handlePrevMonth();
              }} 
              className="p-1"
            >
              <ChevronLeft size={20} />
            </button>
            <span>
              {currentMonth.toLocaleString('default', {
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleNextMonth();
              }} 
              className="p-1"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day labels */}
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className="text-center text-gray-500 text-xs">
                {day}
              </div>
            ))}
            {/* Padding days from previous month */}
            {previousMonthDays.map((_, index) => (
              <div
                key={`prev-${index}`}
                className="text-center text-gray-600 p-1"
              ></div>
            ))}
            {/* Days of the current month */}
            {days.map((day) => (
              <button
                type="button"
                key={day}
                onClick={(e) => {
                  e.preventDefault();
                  handleDateClick(day);
                }}
                className={`text-center p-1 hover:bg-blue-600 rounded ${
                  selectedDate &&
                  selectedDate.getDate() === day &&
                  selectedDate.getMonth() === currentMonth.getMonth() &&
                  selectedDate.getFullYear() === currentMonth.getFullYear()
                    ? 'bg-blue-500'
                    : ''
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Mode descriptions for tooltips
const modeDescriptions = {
  Champion: "See all changes made to a champion since a specific date",
  Summoner: "View updates for your most played champions"
};

// Helper text for date input
const datePickerHelperText = {
  Champion: "Select the date you want to see changes from. We'll show all changes made to this champion after this date.",
  Summoner: "No date needed - we'll use your match history to determine relevant changes."
};

// Main CatchUpLoL Component
export default function App() {
  const [mode, setMode] = useState('Champion'); // 'Champion' or 'Summoner'
  const [champion, setChampion] = useState('');
  const [lastPlayed, setLastPlayed] = useState(null);
  const [error, setError] = useState('');
  const [filteredChanges, setFilteredChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Reset submission state when inputs change
  const handleChampionChange = (newValue) => {
    setChampion(newValue);
    setHasSubmitted(false);
    setError('');
  };

  const handleDateChange = (date) => {
    setLastPlayed(date);
    setHasSubmitted(false);
    setError('');
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!champion || !lastPlayed) {
      setError('Please enter both a champion name and last played date.');
      return;
    }

    if (!champions.includes(champion)) {
      setError('Please select a valid champion from the suggestions.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      console.log(
        'Fetching patch notes for',
        champion,
        'since',
        formatDate(lastPlayed)
      );
      // API call to your backend to fetch patch notes and summary
      const response = await axios.get('/api/patch-notes', {
        params: {
          champion: champion,
          lastPlayed: formatDate(lastPlayed), // Send formatted date to the API
        },
      });

      console.log('API Response:', response);

      if (!response.data) {
        throw new Error('No data received from API');
      }

      if (!response.data.filteredChanges) {
        console.log('Full response:', response);
        throw new Error('No filteredChanges in response');
      }

      // Sort patch notes by date in descending order (newest first)
      const sortedChanges = Array.isArray(response.data.filteredChanges) 
        ? response.data.filteredChanges.sort((a, b) => {
            return new Date(b.Date) - new Date(a.Date);
          })
        : [];

      setFilteredChanges(sortedChanges);

      // Clean up summary text if it exists
      if (response.data.summary) {
        const summaryText = response.data.summary
          .split('Changes Summary:')[0]  // Take only the part before "Changes Summary:"
          .replace(/\*\*/g, '')  // remove stars
          .replace(/\n\s*\n/g, '\n')  // remove extra blank lines
          .trim();
        setSummary(summaryText);
      } else {
        setSummary('No summary available');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });

      let errorMessage = 'An error occurred while fetching patch notes.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.details) {
        errorMessage = error.response.data.details;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setFilteredChanges([]);
      setSummary('');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 flex flex-col items-center p-4">
      <div className="w-full max-w-6xl relative">
        {/* Mode Toggle - Now in top right */}
        <div className="absolute right-0 top-0 z-10">
          <div className="flex items-center bg-gray-800 rounded-lg p-1 border border-gray-700">
            <button
              type="button"
              onClick={() => setMode('Champion')}
              className={`px-4 py-2 rounded-md transition-all duration-200 ${
                mode === 'Champion'
                  ? 'bg-blue-600 text-white font-medium'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center space-x-2">
                <Sword className="w-4 h-4" />
                <span style={{ fontFamily: 'BeaufortForLoL, sans-serif' }}>Champion</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setMode('Summoner')}
              className={`px-4 py-2 rounded-md transition-all duration-200 ${
                mode === 'Summoner'
                  ? 'bg-blue-600 text-white font-medium'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span style={{ fontFamily: 'BeaufortForLoL, sans-serif' }}>Summoner</span>
              </span>
            </button>
          </div>
        </div>

        <h2 
          onClick={() => window.location.reload()}
          className="text-4xl font-bold mb-6 text-center pt-16 cursor-pointer text-white hover:text-blue-200 transition-colors duration-200"
          style={{ fontFamily: 'BeaufortForLoL, sans-serif' }}
        >
          CatchUpLoL
        </h2>

        {/* Mode Toggle */}
        {/* <div className="flex justify-center mb-6">
          <div className="flex items-center bg-gray-800 rounded-md p-1">
            <button
              type="button"
              onClick={() => setMode('Champion')}
              className={`px-4 py-2 rounded-md transition-colors ${
                mode === 'Champion'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Champion
            </button>
            <button
              type="button"
              onClick={() => setMode('Summoner')}
              className={`px-4 py-2 rounded-md transition-colors ${
                mode === 'Summoner'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Summoner
            </button>
          </div>
        </div> */}

        <p className="text-center text-gray-400 mb-6">
          {mode === 'Champion' 
            ? 'See all balance changes made to a champion after your selected date'
            : 'View changes for your most played champions'}
        </p>

        {mode === 'Champion' ? (
          <>
            {/* Champion search form */}
            <form onSubmit={handleSubmit} className="mb-6">
              <div className="flex flex-col space-y-4 w-full max-w-2xl mx-auto">
                {/* Champion Input */}
                <div className="w-full">
                  <label htmlFor="champion" className="block text-sm font-medium text-gray-300 mb-1">
                    Select Champion
                  </label>
                  <ChampionAutocomplete
                    champions={champions}
                    value={champion}
                    onChange={handleChampionChange}
                  />
                </div>
                {/* Custom date picker */}
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Show Changes After This Date
                  </label>
                  <CustomDatePicker
                    selectedDate={lastPlayed}
                    onChange={handleDateChange}
                  />
                  <p className="mt-1 text-sm text-gray-400">
                    {datePickerHelperText[mode]}
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center mt-4">
                <button
                  type="submit"
                  className={`px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors
                    ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : mode === 'Champion' ? 'Show Changes After Selected Date' : 'Search Changes'}
                </button>
              </div>
            </form>
            {/* Error message display */}
            {error && (
              <div className="text-red-400 flex items-center mb-4 justify-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                {error}
              </div>
            )}
            {/* Loading indicator */}
            {loading && (
              <div className="flex flex-col items-center justify-center mb-8">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse delay-150"></div>
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse delay-300"></div>
                </div>
                <span className="mt-2 text-gray-300">Fetching champion updates...</span>
              </div>
            )}
            {/* Container for both summary and changes */}
            <div className="max-w-4xl mx-auto">
              {/* Display summary only when there are filtered changes */}
              {summary && filteredChanges && filteredChanges.length > 0 && (
                <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-6">
                  <h2 className="text-xl font-semibold mb-4 text-white">Champion Changes</h2>
                  <ReactMarkdown className="text-gray-300 prose prose-invert max-w-none">
                    {summary}
                  </ReactMarkdown>
                </div>
              )}
              {/* Display filtered changes */}
              {filteredChanges && filteredChanges.length > 0 ? (
                <div className="space-y-6 overflow-y-auto max-h-[60vh]">
                  {filteredChanges.map((change, index) => (
                  <div key={index} className="bg-gray-800 p-4 rounded-md">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-400">
                        {formatDate(new Date(change.Date))}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          change['Change Type'] === 'Buff'
                            ? 'bg-green-900 text-green-200'
                            : change['Change Type'] === 'Nerf'
                            ? 'bg-red-900 text-red-200'
                            : 'bg-blue-900 text-blue-200'
                        }`}
                      >
                        {change['Change Type']}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-100 mb-2">
                      {change.Patchnotes}
                    </h3>
                    <p className="text-sm text-gray-300 mb-2">
                      {change['Change Summary']}
                    </p>
                    <pre className="text-xs bg-gray-700 p-2 rounded overflow-x-auto whitespace-pre-wrap text-gray-300">
                      {change['Change Description'].split(';').map((stat, i) => (
                        <React.Fragment key={i}>
                          {stat.trim()}
                          {i < change['Change Description'].split(';').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </pre>
                  </div>
                ))}
                </div>
              ) : hasSubmitted && !loading && !error ? (
                <p className="text-gray-300 text-center">
                  No changes found for {champion} since {formatDate(lastPlayed)}.
                </p>
              ) : null}
            </div>
          </>
        ) : (
          <SummonerPage />
        )}
      </div>
    </div>
  );
}